import { useDispatch } from 'react-redux';
import { download } from '../../common/DownloadFileController/downloadFile';
import {
  apiMultiDownloadProductV2,
  apiPatchPublicImageMeasurementData,
  apiUpdateNameProductCategory360Viewer,
  apiUpdateNameProductCategoryConfigurator,
  apiUpdateNameProductCategoryGif,
  apiUpdateNameProductCategoryImages,
  apiUpdateNameProductCategoryVideo,
  apiUpdateNameTextureCategory360Viewer,
  apiUpdateNameTextureCategoryConfigurator,
  apiUpdateNameTextureCategoryGif,
  apiUpdateNameTextureCategoryImages,
  apiUpdateNameTextureCategoryVideo,
  apiUpdateProductCategory360Viewer,
  apiUpdateProductCategoryConfigurator,
  apiUpdateProductCategoryGif,
  apiUpdateProductCategoryImages,
  apiUpdateProductCategoryVideo,
  apiUpdateTextureCategory360Viewer,
  apiUpdateTextureCategoryConfigurator,
  apiUpdateTextureCategoryGif,
  apiUpdateTextureCategoryImages,
  apiUpdateTextureCategoryVideo,
  cancellableApiPostImageOnPresignedData,
  UpdateProductPublicShare,
  UpdateTexturePublicShare,
} from '../../helpers/api';
import { successToast, warningToast } from '../../helpers/toastHelper';
import { checkIfMemberHaveEditorAccess, useUserContext } from '../../contexts/UserContext';
import { getCuratorRenderPreviewPath } from '../../helpers/urlHelper';
import { setTemporaryPreventBackupPopup } from '../../redux/slicers/admin/curatorSlicer';
import { useState } from 'react';
import {
  setShowEditorNudge,
  setTriggerProductContentUpdateForPresentationView,
} from '../../redux/slicers/productV2Slicer';
import {
  base64ToBlob,
  getFileExtension,
  getProductPageType,
  isImageBlankOrTransparent,
} from '../../helpers/jsHelper';
import domtoimage from 'dom-to-image-more';
import { updatePublicLinkProductTexture } from './ShareButtonV2/helpers';
import { Mixpanel, MxEvent } from '../../common/Mixpanel';
import { orderBy } from 'lodash';

const contentNameUpdateAPIMapping = {
  [getProductPageType().PRODUCTIMAGE]: apiUpdateNameProductCategoryImages,
  [getProductPageType().THREESIXTY]: apiUpdateNameProductCategory360Viewer,
  [getProductPageType().DIMENSIONSHOT]: apiUpdateNameProductCategoryImages,
  [getProductPageType().GIF]: apiUpdateNameProductCategoryGif,
  [getProductPageType().VIDEO]: apiUpdateNameProductCategoryVideo,
  [getProductPageType().CONFIGURATOR]: apiUpdateNameProductCategoryConfigurator,
};

const contentUpdateAPIMapping = {
  [getProductPageType().PRODUCTIMAGE]: apiUpdateProductCategoryImages,
  [getProductPageType().THREESIXTY]: apiUpdateProductCategory360Viewer,
  [getProductPageType().DIMENSIONSHOT]: apiUpdateProductCategoryImages,
  [getProductPageType().GIF]: apiUpdateProductCategoryGif,
  [getProductPageType().VIDEO]: apiUpdateProductCategoryVideo,
  [getProductPageType().CONFIGURATOR]: apiUpdateProductCategoryConfigurator,
};

const contentNameTextureUpdateAPIMapping = {
  [getProductPageType().PRODUCTIMAGE]: apiUpdateNameTextureCategoryImages,
  [getProductPageType().THREESIXTY]: apiUpdateNameTextureCategory360Viewer,
  [getProductPageType().GIF]: apiUpdateNameTextureCategoryGif,
  [getProductPageType().VIDEO]: apiUpdateNameTextureCategoryVideo,
  [getProductPageType().CONFIGURATOR]: apiUpdateNameTextureCategoryConfigurator,
};

const contentUpdateTextureAPIMapping = {
  [getProductPageType().PRODUCTIMAGE]: apiUpdateTextureCategoryImages,
  [getProductPageType().THREESIXTY]: apiUpdateTextureCategory360Viewer,
  [getProductPageType().GIF]: apiUpdateTextureCategoryGif,
  [getProductPageType().VIDEO]: apiUpdateTextureCategoryVideo,
  [getProductPageType().CONFIGURATOR]: apiUpdateTextureCategoryConfigurator,
};

// The DIMENSIONSHOT list API serializer doesn't include is_transparent_bg inside
// render_image_details. Fall back to the render file URL extension: if the renderer
// saved the file as .png it was a transparent-background render.
export const isDimensionShotTransparent = (item) => {
  if (item?.render_image_details?.is_transparent_bg) return true;
  const renderFile = item?.render_image_details?.file || '';
  return /\.png(\?|$)/i.test(renderFile);
};

const useCategoryContentActions = ({
  category,
  productInfo,
  row,
  list = [],
  setList,
  isTexture,
  isPublicPage = false,
  publicShare = false,
  orgProductUniqueCodeId,
}) => {
  const dispatch = useDispatch();
  const { isEditor, isGuestUser } = useUserContext();
  const [loading, setLoading] = useState({
    delete: false,
    toggle: false,
    download: false,
  });

  /**
   * Opens the 3D preview of a product image in a new window.
   *
   * @param {Object} [params] - The parameters for the open in 3D operation.
   * @param {function} [params.callback] - An optional callback function to be called after the open in 3D operation is complete.
   */
  const onOpenIn3D = ({ callback } = {}) => {
    if (
      [getProductPageType().PRODUCTIMAGE, getProductPageType().DIMENSIONSHOT].includes(category)
    ) {
      if (!checkIfMemberHaveEditorAccess(isGuestUser() ? true : false)) {
        !isGuestUser() && dispatch(setShowEditorNudge(true));
        return;
      }
      if (row?.render_image_details) {
        Mixpanel.track(true, MxEvent.EDIT_IN_3D_IMAGE, {
          image_id: row?.render_image_details?.main_render,
          image_name: row?.render_image_details?.angle_name,
          image_angle_name: row?.render_image_details?.angle_name,
        });

        dispatch(
          setTemporaryPreventBackupPopup({
            type: 'room',
            id: row?.render_image_details?.main_render,
          })
        );
        const url = getCuratorRenderPreviewPath(
          row?.render_image_details?.main_render,
          row?.render_image_details?.angle_name
        );
        window.open(`${url}&isFromProductPage=true`, '_blank');
      }
    }
  };

  /**
   * Delete a product category content.
   *
   * @param {Object} [params] - The parameters for the delete operation.
   * @param {function} [params.callback] - An optional callback function to be called after the delete operation is complete.
   */
  const onDelete = async ({ data, callback } = {}) => {
    if (!isEditor && !checkIfMemberHaveEditorAccess(false)) {
      if (isGuestUser()) {
        warningToast('You do not have permission to perform this action.');
      } else {
        dispatch(setShowEditorNudge(true));
      }
      return;
    }

    setLoading((prev) => ({ ...prev, delete: true }));
    const objData = isTexture ? contentUpdateTextureAPIMapping : contentUpdateAPIMapping;
    const response = await objData?.[category]?.({
      id: productInfo.id,
      data: JSON.stringify(data),
    });

    if (response?.status) {
      successToast(`Item ${data?.[0]?.is_archive ? 'archived' : 'unarchived'} successfully`);
      callback?.();
      updatePublicLink();
      dispatch(setTriggerProductContentUpdateForPresentationView(category));
    }

    setLoading((prev) => ({ ...prev, delete: false }));
  };

  const onShare = ({ callback } = {}) => {};

  const onAddToCollection = ({ callback } = {}) => {};

  const onRemoveWatermark = ({ callback } = {}) => {};

  /**
   * Downloads a file based on the product category type.
   *
   * @param {Object} params - The parameters for the download operation.
   * @param {function} [params.callback] - An optional callback function to be called after the download operation is complete.
   */
  const onDownload = async ({ callback } = {}) => {
    if (!isPublicPage && !isEditor && !checkIfMemberHaveEditorAccess(false)) {
      if (isGuestUser()) {
        warningToast('You do not have permission to perform this action.');
      } else {
        dispatch(setShowEditorNudge(true));
      }
      return;
    }

    // Transparent dimension shots must be captured live from the DOM so the alpha
    // channel is preserved. Downloading download_file_link would give the old JPEG
    // (or a PNG with the checkerboard baked in from a prior upload).
    // The DIMENSIONSHOT list serializer doesn't expose is_transparent_bg, so we fall
    // back to checking the render file URL extension (.png = transparent render).
    if (
      category === getProductPageType().DIMENSIONSHOT &&
      isDimensionShotTransparent(row)
    ) {
      const uploaderEl = document.getElementById('lf-dimension-shot-image-viewer-for-upload');
      const target = Array.from(document.querySelectorAll('.lf-zoom-image-container')).find(
        (el) => !uploaderEl?.contains(el)
      );
      if (target) {
        const { clientWidth, clientHeight } = target;
        const scale = clientWidth < 1920 || clientHeight < 1080 ? 2 : 1;
        const captureOptions = {
          width: clientWidth * scale,
          height: clientHeight * scale,
          bgcolor: 'rgba(0,0,0,0)',
          style: {
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: `${clientWidth}px`,
            height: `${clientHeight}px`,
          },
        };
        const fileName = `${row?.display_name || 'image'}.png`;
        const checkerEls = Array.from(target.querySelectorAll('.lf-checker-png'));
        checkerEls.forEach((el) => el.classList.remove('lf-checker-png'));
        const capturePromise = domtoimage.toPng(target, captureOptions);
        const restore = () => checkerEls.forEach((el) => el.classList.add('lf-checker-png'));
        requestAnimationFrame(restore);
        capturePromise
          .then((dataUrl) => {
            const link = document.createElement('a');
            link.download = fileName;
            link.href = dataUrl;
            link.click();
            callback?.();
          })
          .catch(restore);
        return;
      }
    }

    if (
      [
        getProductPageType().PRODUCTIMAGE,
        getProductPageType().DIMENSIONSHOT,
        getProductPageType().GIF,
        getProductPageType().VIDEO,
      ].includes(category) &&
      row?.download_file_link
    ) {
      const extension = getFileExtension(row?.download_file_link) || (row?.render_image_details?.is_transparent_bg ? '.png' : row?.download_extension);
      await download({
        fileUrl: row?.download_file_link,
        fileName: `${row?.display_name}${extension}`,
        callback,
      });
    }
  };

  /**
   * Toggles the state of a product category content.
   *
   * @param {Object} params - The parameters for the toggle operation.
   * @param {function} [params.callback] - An optional callback function to be called after the toggle operation is complete.
   * @param {Object} [params.data] - The data to be used for the toggle operation.
   */
  const onToggle = async ({ callback, data } = {}) => {
    if (!isEditor && !checkIfMemberHaveEditorAccess(false)) {
      if (isGuestUser()) {
        warningToast('You do not have permission to perform this action.');
      } else {
        dispatch(setShowEditorNudge(true));
      }
      return;
    }

    setLoading((prev) => ({ ...prev, toggle: true }));
    const objData = isTexture ? contentUpdateTextureAPIMapping : contentUpdateAPIMapping;
    const response = await objData?.[category]?.({
      id: productInfo.id,
      data: JSON.stringify(data),
    });

    if (response?.status) {
      updatePublicLink();
      callback?.();
      dispatch(setTriggerProductContentUpdateForPresentationView(category));
    }

    setLoading((prev) => ({ ...prev, toggle: false }));
  };

  /**
   * Updates the order of product category content.
   *
   * @param {Object} params - The parameters for the update order operation.
   * @param {Array} [params.data=[]] - The array of items to update the order for.
   * @param {function} [params.callback] - An optional callback function to be called after the update order operation is complete.
   */
  const onUpdateOrder = async ({ data = [], callback } = {}) => {
    if (!isEditor && !checkIfMemberHaveEditorAccess(false)) {
      if (isGuestUser()) {
        warningToast('You do not have permission to perform this action.');
      } else {
        dispatch(setShowEditorNudge(true));
      }
      return;
    }

    if (!productInfo?.id || data?.length === 0) return;
    setList?.(data);
    const objData = isTexture ? contentUpdateTextureAPIMapping : contentUpdateAPIMapping;
    const response = await objData?.[category]?.({
      id: productInfo.id,
      data: JSON.stringify(
        data.map((item) => ({
          id: item.id,
          order: item.seq_no,
        }))
      ),
    });

    if (response?.status) {
      callback?.();
      updatePublicLink();
      dispatch(setTriggerProductContentUpdateForPresentationView(category));
    }
  };

  /**
   * Renames a product category image or dimension shot.
   *
   * @param {Object} params - The parameters for the rename operation.
   * @param {string} params.id - The ID of the product category content.
   * @param {string} params.name - The new name for the product category content.
   * @param {Object} params.item - The item being renamed.
   * @param {function} [params.callback] - An optional callback function to be called after the rename operation is complete.
   */
  const onRename = async ({ id, name, item, callback }) => {
    if (!isEditor && !checkIfMemberHaveEditorAccess(false)) {
      if (isGuestUser()) {
        warningToast('You do not have permission to perform this action.');
      } else {
        dispatch(setShowEditorNudge(true));
      }
      return;
    }

    if (!id || !name) {
      warningToast('Please enter a name.');
      return;
    }
    const objData = isTexture ? contentNameTextureUpdateAPIMapping : contentNameUpdateAPIMapping;
    objData?.[category]?.({
      id: productInfo.id,
      content_id: id,
      data: JSON.stringify({
        name,
      }),
    }).then(() => callback?.());
  };

  const getItemsSeparatedByCategory = (l = []) => {
    const { PRODUCTIMAGE, THREESIXTY, DIMENSIONSHOT, VIDEO, CONFIGURATOR, GIF } =
      getProductPageType();

    if (!l?.length) return {};

    return {
      [PRODUCTIMAGE]: l?.filter((i) => i?.category === PRODUCTIMAGE),
      [THREESIXTY]: l?.filter((i) => i?.category === THREESIXTY),
      [DIMENSIONSHOT]: l?.filter((i) => i?.category === DIMENSIONSHOT),
      [VIDEO]: l?.filter((i) => i?.category === VIDEO),
      [CONFIGURATOR]: l?.filter((i) => i?.category === CONFIGURATOR),
      [GIF]: l?.filter((i) => i?.category === GIF),
    };
  };

  const updatePublicLink = () => {
    updatePublicLinkProductTexture({ publicShare, isTexture, productId: productInfo?.id });
  };

  const onMultiDelete = async ({ selectedItems, callback }) => {
    if (!isEditor && !checkIfMemberHaveEditorAccess(false)) {
      if (isGuestUser()) {
        warningToast('You do not have permission to perform this action.');
      } else {
        dispatch(setShowEditorNudge(true));
      }
      return;
    }

    const categoriesContent = getItemsSeparatedByCategory(selectedItems);
    setLoading((prev) => ({ ...prev, delete: true }));

    const response = await Promise.allSettled(
      Object.keys(categoriesContent)?.map(async (cat) => {
        const ids = categoriesContent?.[cat]?.filter((a) => !a?.__no_delete)?.map((i) => i.id);
        if (ids?.length === 0) return;
        const objData = isTexture ? contentUpdateTextureAPIMapping : contentUpdateAPIMapping;
        const func = objData?.[cat];
        return await func?.({
          id: productInfo.id,
          data: JSON.stringify(
            ids?.map((a) => ({
              id: a,
              is_archive: true,
            }))
          ),
        });
      })
    );

    setLoading((prev) => ({ ...prev, delete: false }));
    if (response.some((i) => i?.value)) {
      successToast('Archived successfully');
      callback?.();
      updatePublicLink();
      dispatch(setTriggerProductContentUpdateForPresentationView(category));
    }
  };

  const onMultiDownload = async ({ selectedItems, callback }) => {
    if (!isEditor && !checkIfMemberHaveEditorAccess(false)) {
      if (isGuestUser()) {
        warningToast('You do not have permission to perform this action.');
      } else {
        dispatch(setShowEditorNudge(true));
      }
      return;
    }

    setLoading((prev) => ({ ...prev, download: true }));
    const formData = new FormData();
    selectedItems
      ?.filter((a) => !a?.__no_download)
      ?.map(async (item) => {
        const key =
          item.category === getProductPageType().GIF
            ? 'gif'
            : item.category === getProductPageType().VIDEO
            ? 'video'
            : isTexture
            ? 'texture_images'
            : 'product_images';
        formData.append(key, item.id);
      });
    const response = await apiMultiDownloadProductV2({ isTexture, formData });
    if (response?.status) {
      successToast(
        response?.message ||
          'Your file is being processed and will be delivered to your email address soon.'
      );
    }
    callback?.();
    setLoading((prev) => ({ ...prev, download: false }));
  };

  const onMoveToTop = (e, id) => {
    if (!isEditor && !checkIfMemberHaveEditorAccess(false)) {
      if (isGuestUser()) {
        warningToast('You do not have permission to perform this action.');
      } else {
        dispatch(setShowEditorNudge(true));
      }
      return;
    }

    if (!list || list?.length === 0 || !id) return;
    e?.preventDefault();
    e?.stopPropagation();
    const row = list?.find((item) => item.id === id);
    const rest = list.filter((x) => x.id !== id);

    // Keep the relative order of other items based on current seq_no (top = highest)
    const restSorted = orderBy(rest, [(x) => Number(x.seq_no) || 0], ['desc']);

    // New UI order: moved item first, then others
    const ordered = [row, ...restSorted];

    // Reassign seq_no so first has MAX, last has 1 (contiguous, no duplicates)
    const n = ordered.length;
    const _list = ordered.map((item, idx) => ({
      ...item,
      seq_no: n - idx, // top gets n (max), next gets n-1 ... last gets 1
    }));

    // setList?.(_list);
    onUpdateOrder({
      data: _list,
      fetchData: false,
    });
  };

  // dimension shot upload & download working - begins

  const uploadDimensionShotImages = async ({
  id,
  name,
  base64 = null,
  isTransparent = false,
  callback = null,
  ref = null,
}) => {
  if (!id || !name || !base64) return;
  const payload = { dimension_shot_key: name };
  const mimeType = isTransparent ? 'image/png' : 'image/jpeg';
  const file = base64ToBlob({
    imageString: base64?.replace('data:image/png;base64,', ''),
    mimeType,
  });
    const response = await apiPatchPublicImageMeasurementData(id, payload);
    if (response?.presigned_data && file) {
      const uploadResponse = await cancellableApiPostImageOnPresignedData(
        file,
        response.presigned_data,
        () => {},
        ref?.current?.signal
      );
      if (uploadResponse && [200, 204]?.includes(uploadResponse?.status)) {
        await apiPatchPublicImageMeasurementData(id, { is_dimension_shot_file: true }); // making is_dimension_shot_file: true when the image is successfully uploaded
      }
      callback?.();
    }
  };

  const onDownloadAndUploadDimensionShot = ({
  target = null,
  item = null,
  callback = null,
  ref = null,
} = {}) => {
  const _item = item || row;
  if (!target || !_item) return;

  const fileName = `${_item.display_name || 'image'}${item.download_extension}`;
  const allComponent = document.querySelector(target);

  const isTransparent = isDimensionShotTransparent(_item);

  if (allComponent) {
    const { clientHeight, clientWidth } = allComponent;
    const scale = clientWidth < 1920 || clientHeight < 1080 ? 2 : 1;
    const styleObj = {
      width: clientWidth * scale,
      height: clientHeight * scale,
      style: {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        width: `${clientWidth}px`,
        height: `${clientHeight}px`,
      },
    };

    // For transparent renders, temporarily strip the checkerboard CSS background
    // so domtoimage captures true transparency instead of baking it in.
    const checkerEls = isTransparent
      ? Array.from(allComponent.querySelectorAll('.lf-checker-png'))
      : [];
    checkerEls.forEach((el) => el.classList.remove('lf-checker-png'));

    const captureOptions = isTransparent
      ? { ...styleObj, bgcolor: 'rgba(0,0,0,0)' }
      : styleObj;

    domtoimage
      ?.toPng(allComponent, captureOptions)
      .then((dataUrl) => {
        checkerEls.forEach((el) => el.classList.add('lf-checker-png'));
        const img = new Image();
        img.onload = () => {
          isImageBlankOrTransparent(dataUrl, (result) => {
            if (!result) return;
            uploadDimensionShotImages({
              id: _item?.dimension_shot_image_details?.id,
              name: fileName,
              base64: dataUrl,
              isTransparent,
              callback,
              ref,
            });
          });
        };
        img.onerror = () => {
          if (callback) callback(new Error('Image failed to load'));
        };
        img.src = dataUrl;
      })
      .catch((error) => {
        checkerEls.forEach((el) => el.classList.add('lf-checker-png'));
        console.error('Error generating image:', error);
        if (callback) {
          callback(error);
        }
      });
    }
  };

  // dimension shot upload & download working - ends

  return {
    onOpenIn3D,
    onShare,
    onAddToCollection,
    onRemoveWatermark,
    onDownload,
    onDelete,
    deleteLoading: loading?.delete,
    onToggle,
    toggleLoading: loading?.toggle,
    onUpdateOrder,
    onRename,
    onMultiDelete,
    onMultiDownload,
    downloadLoading: loading?.download,
    onMoveToTop,
    onDownloadAndUploadDimensionShot,
    updatePublicLink,
  };
};

export default useCategoryContentActions;
